// src/admin/manage-affiliates/ManageAffiliatesPage.js

import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { 
  DocumentDuplicateIcon, 
  TrashIcon, 
  PlusCircleIcon, 
  ChartBarIcon, 
  CheckIcon, 
  ShoppingCartIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const ManageAffiliatesPage = () => {
    const [affiliates, setAffiliates] = useState([]);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchAffiliates = async () => {
            setLoading(true);
            try {
                const querySnapshot = await getDocs(collection(db, 'affiliates'));
                const list = querySnapshot.docs.map(docItem => ({
                    id: docItem.id,
                    ...docItem.data()
                }));
                list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                setAffiliates(list);
            } catch (err) {
                console.error("Error fetching affiliates:", err);
                setError("Failed to load affiliate records.");
            } finally {
                setLoading(false);
            }
        };

        fetchAffiliates();
    }, []);

    const generateRefCode = (str) => {
        const cleanStr = str
            .toLowerCase()
            .trim()
            .replace(/[\s\W-]+/g, '-')
            .replace(/^-+|-+$/g, '');
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        return `${cleanStr}-${randomNum}`;
    };

    const handleAddAffiliate = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;

        setSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const refCode = generateRefCode(name);
            const targetUrl = `https://studio.littlehibba.com/publishing-program?ref=${refCode}`;
            
            const newDoc = {
                name: name.trim(),
                refCode: refCode,
                url: targetUrl,
                clicks: 0,
                conversions: 0, // <-- STRIPE SATIŞLARI İÇİN YENİ ALAN
                createdAt: new Date().toISOString()
            };

            const docRef = await addDoc(collection(db, 'affiliates'), newDoc);
            setAffiliates(prev => [{ id: docRef.id, ...newDoc }, ...prev]);
            setName('');
            setSuccess('New partner link generated successfully!');
        } catch (err) {
            console.error("Error creating affiliate:", err);
            setError("An error occurred while generating the affiliate link.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = (id, url) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2500);
    };

    const handleDelete = async (id, affiliateName) => {
        if (window.confirm(`Are you sure you want to permanently delete the affiliate tracking for "${affiliateName}"?`)) {
            try {
                await deleteDoc(doc(db, 'affiliates', id));
                setAffiliates(prev => prev.filter(item => item.id !== id));
            } catch (err) {
                console.error("Error deleting affiliate:", err);
                alert("Failed to delete affiliate record.");
            }
        }
    };

    // İstatistik Hesaplamaları
    const totalClicks = affiliates.reduce((sum, item) => sum + (item.clicks || 0), 0);
    const totalSales = affiliates.reduce((sum, item) => sum + (item.conversions || 0), 0);
    const avgConversionRate = totalClicks > 0 ? ((totalSales / totalClicks) * 100).toFixed(1) : '0.0';

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            {/* Page Header */}
            <div className="mb-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                <div>
                    <h1 className="fw-bold fs-3 mb-1" style={{ color: '#1e293b' }}>Partner & Affiliate Performance</h1>
                    <p className="text-muted small mb-0">Monitor tracking links, incoming traffic, and verified Stripe checkouts.</p>
                </div>
                <div className="badge bg-white text-secondary border px-3 py-2 rounded-pill shadow-sm fs-6 fw-medium">
                    <span className="d-inline-block bg-success rounded-circle me-2" style={{ width: '8px', height: '8px' }}></span>
                    Live Tracking Active
                </div>
            </div>

            {error && <div className="alert alert-danger rounded-4 shadow-sm border-0">{error}</div>}
            {success && <div className="alert alert-success rounded-4 shadow-sm border-0">{success}</div>}

            {/* 4-COLUMN SYMMETRICAL METRICS ROW (Clean Light Architecture) */}
            <div className="row g-4 mb-4">
                <div className="col-xl-3 col-md-6">
                    <div className="card bg-white border-0 shadow-sm rounded-4 p-4 h-100 d-flex justify-content-between">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <span className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Total Partners</span>
                            <div className="p-2 bg-primary bg-opacity-10 text-primary rounded-3">
                                <UserGroupIcon style={{ width: '22px' }} />
                            </div>
                        </div>
                        <h2 className="display-6 fw-bold m-0" style={{ color: '#0f172a' }}>{affiliates.length}</h2>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6">
                    <div className="card bg-white border-0 shadow-sm rounded-4 p-4 h-100 d-flex justify-content-between">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <span className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Total Clicks</span>
                            <div className="p-2 bg-info bg-opacity-10 text-info rounded-3">
                                <ChartBarIcon style={{ width: '22px' }} />
                            </div>
                        </div>
                        <h2 className="display-6 fw-bold m-0" style={{ color: '#0f172a' }}>{totalClicks}</h2>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6">
                    <div className="card bg-white border-0 shadow-sm rounded-4 p-4 h-100 d-flex justify-content-between">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <span className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Verified Sales</span>
                            <div className="p-2 bg-success bg-opacity-10 text-success rounded-3">
                                <ShoppingCartIcon style={{ width: '22px' }} />
                            </div>
                        </div>
                        <h2 className="display-6 fw-bold m-0 text-success">{totalSales}</h2>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6">
                    <div className="card bg-white border-0 shadow-sm rounded-4 p-4 h-100 d-flex justify-content-between">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <span className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Avg. Conv. Rate</span>
                            <div className="p-2 bg-warning bg-opacity-10 text-warning rounded-3">
                                <SparklesIcon style={{ width: '22px' }} />
                            </div>
                        </div>
                        <h2 className="display-6 fw-bold m-0" style={{ color: '#d97706' }}>%{avgConversionRate}</h2>
                    </div>
                </div>
            </div>

            {/* Generator Form Card */}
            <div className="card bg-white border-0 shadow-sm rounded-4 mb-4">
                <div className="card-header bg-white py-3 px-4 border-bottom border-light d-flex align-items-center gap-2">
                    <PlusCircleIcon style={{ width: '20px' }} className="text-primary" />
                    <h6 className="fw-bold m-0" style={{ color: '#1e293b' }}>Generate New Tracking Link</h6>
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleAddAffiliate}>
                        <div className="row g-3 align-items-end">
                            <div className="col-md-9">
                                <label htmlFor="affiliateName" className="form-label small fw-bold text-secondary">
                                    PARTNER / INFLUENCER NAME *
                                </label>
                                <input 
                                    type="text" 
                                    id="affiliateName" 
                                    className="form-control form-control-lg bg-light border-0 px-3 fs-6 rounded-3" 
                                    placeholder="e.g., Islamic Teachers Network or Sister Jane"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-3">
                                <button 
                                    type="submit" 
                                    className="btn btn-primary btn-lg w-100 fw-bold rounded-3 shadow-sm fs-6"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Creating...' : '+ Create Link'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Affiliate Links & Stats Table */}
            <div className="card bg-white border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-header bg-white py-4 px-4 border-bottom border-light">
                    <h6 className="fw-bold m-0" style={{ color: '#1e293b' }}>Active Tracking Links</h6>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5 text-muted small">Loading partner records...</div>
                    ) : affiliates.length === 0 ? (
                        <div className="text-center py-5 text-muted small">No active links. Create your first partner above.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 border-light">
                                <thead className="bg-light text-secondary text-uppercase small fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                    <tr>
                                        <th className="py-3 px-4 border-0">Partner Name</th>
                                        <th className="py-3 border-0">Generated Link</th>
                                        <th className="py-3 text-center border-0">Clicks</th>
                                        <th className="py-3 text-center border-0">Stripe Sales</th>
                                        <th className="py-3 text-center border-0">Rate</th>
                                        <th className="py-3 border-0">Created</th>
                                        <th className="py-3 px-4 text-end border-0">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {affiliates.map((item) => {
                                        const c = item.clicks || 0;
                                        const s = item.conversions || 0;
                                        const rate = c > 0 ? ((s / c) * 100).toFixed(1) : '0.0';

                                        return (
                                            <tr key={item.id} style={{ transition: 'all 0.2s' }}>
                                                <td className="px-4 py-3 border-light">
                                                    <div className="fw-bold text-dark">{item.name}</div>
                                                    <div className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>ref: {item.refCode}</div>
                                                </td>
                                                <td className="border-light">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm bg-light text-muted font-monospace border-0" 
                                                            value={item.url} 
                                                            readOnly 
                                                            style={{ maxWidth: '320px', fontSize: '0.8rem' }}
                                                        />
                                                        <button 
                                                            onClick={() => handleCopy(item.id, item.url)} 
                                                            className={`btn btn-sm px-3 rounded-2 fw-medium d-flex align-items-center gap-1 ${copiedId === item.id ? 'btn-success text-white' : 'btn-light text-primary border'}`}
                                                            style={{ fontSize: '0.8rem' }}
                                                        >
                                                            {copiedId === item.id ? <><CheckIcon style={{ width: '14px' }} /> Copied</> : <><DocumentDuplicateIcon style={{ width: '14px' }} /> Copy</>}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="text-center border-light">
                                                    <span className="badge bg-info bg-opacity-10 text-info px-3 py-1 rounded-pill fw-bold fs-6">
                                                        {c}
                                                    </span>
                                                </td>
                                                <td className="text-center border-light">
                                                    <span className={`badge px-3 py-1 rounded-pill fw-bold fs-6 ${s > 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                                                        {s}
                                                    </span>
                                                </td>
                                                <td className="text-center border-light">
                                                    <span className="badge bg-warning bg-opacity-10 text-warning px-2 py-1 rounded-2 fw-bold small">
                                                        %{rate}
                                                    </span>
                                                </td>
                                                <td className="text-muted small border-light" style={{ fontSize: '0.85rem' }}>
                                                    {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </td>
                                                <td className="px-4 text-end border-light">
                                                    <button 
                                                        onClick={() => handleDelete(item.id, item.name)} 
                                                        className="btn btn-sm btn-outline-danger rounded-2 p-1 border-0"
                                                        title="Delete tracking link"
                                                    >
                                                        <TrashIcon style={{ width: '18px' }} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageAffiliatesPage;